import { useEffect, useState } from "react";

/**
 * Approval queue card — shows the newest pending proposal (or a placeholder),
 * a live countdown when auto_mode is on, and approve/reject actions.
 */
export default function ApprovalQueue({
  proposals,
  settings,
  onApprove,
  onReject,
  busy,
}) {
  const pending = (proposals || []).filter((p) => p.status === "pending");
  const top = pending[0];
  return (
    <div data-testid="approval-queue" className="space-y-2">
      <div className="flex items-baseline justify-between">
        <div className="aurion-section-title">Approval Queue</div>
        <div className="font-mono text-[10px] text-[var(--aurion-muted)]">
          {pending.length} pending
        </div>
      </div>
      {top ? (
        <ProposalCard
          proposal={top}
          autoOn={settings?.auto_mode}
          onApprove={onApprove}
          onReject={onReject}
          busy={busy}
        />
      ) : (
        <div
          data-testid="approval-empty"
          className="border border-dashed border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] py-6 text-center text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]"
        >
          — awaiting proposal —
        </div>
      )}
      {pending.length > 1 ? (
        <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
          + {pending.length - 1} more queued
        </div>
      ) : null}
    </div>
  );
}

function ProposalCard({ proposal, autoOn, onApprove, onReject, busy }) {
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, Math.floor((new Date(proposal.expires_at).getTime() - Date.now()) / 1000)),
  );
  useEffect(() => {
    setRemaining(
      Math.max(0, Math.floor((new Date(proposal.expires_at).getTime() - Date.now()) / 1000)),
    );
    const iv = setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => clearInterval(iv);
  }, [proposal.id, proposal.expires_at]);

  const side = String(proposal.side || "").toUpperCase();
  const sideColor =
    side === "BUY" ? "var(--aurion-green)" : "var(--aurion-red)";

  return (
    <div
      data-testid={`proposal-card-${proposal.id}`}
      data-status={proposal.status}
      className="space-y-3 border border-[var(--aurion-gold)]/40 bg-[var(--aurion-panel-2)] p-3"
    >
      <div className="flex items-baseline justify-between">
        <div className="flex items-baseline gap-2">
          <span
            style={{ color: sideColor }}
            className="font-heading text-sm font-semibold tracking-widest"
          >
            {side}
          </span>
          <span className="font-mono text-sm text-[var(--aurion-text)]">
            {proposal.symbol}
          </span>
        </div>
        <div className="font-mono text-[10px] text-[var(--aurion-muted)]">
          {proposal.rating} · {proposal.conviction}%
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-[11px]">
        <Metric label="Qty" value={String(proposal.suggested_qty)} />
        <Metric
          label="Est. $"
          value={`$${Number(proposal.suggested_qty * (proposal.quoted_price || 0)).toFixed(2)}`}
        />
        <Metric label="Target %" value={`${proposal.target_allocation_pct}%`} />
        <Metric
          label="Threshold"
          value={`${proposal.conviction_threshold}%`}
        />
      </div>

      <div className="text-[11px] leading-snug text-[var(--aurion-muted-strong)]">
        {proposal.rationale}
      </div>

      {autoOn ? (
        <div
          data-testid={`proposal-countdown-${proposal.id}`}
          className="flex items-center justify-between border-t border-[var(--aurion-border)] pt-2 text-[10px] uppercase tracking-widest"
        >
          <span className="text-[var(--aurion-muted)]">
            Auto-decide in
          </span>
          <span
            className={`font-mono text-sm ${
              remaining <= 5 ? "text-[var(--aurion-red)]" : "text-[var(--aurion-gold)]"
            }`}
          >
            {remaining}s
          </span>
        </div>
      ) : (
        <div className="border-t border-[var(--aurion-border)] pt-2 text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
          Manual approval only
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 pt-1">
        <button
          data-testid={`approve-${proposal.id}`}
          disabled={busy}
          onClick={() => onApprove(proposal)}
          className="aurion-btn-green px-3 py-2 text-xs uppercase tracking-widest"
        >
          Approve
        </button>
        <button
          data-testid={`reject-${proposal.id}`}
          disabled={busy}
          onClick={() => onReject(proposal)}
          className="aurion-btn-red px-3 py-2 text-xs uppercase tracking-widest"
        >
          Reject
        </button>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="aurion-section-title">{label}</div>
      <div className="font-mono text-[var(--aurion-text)]">{value}</div>
    </div>
  );
}
